import React from "react";
import { MenuCard } from "../menu/MenuCard";
import { MenuCardLabel } from "../menu/MenuCardLabel";
import { useTranslation } from "react-i18next";
import { MenuCardSubLabel } from "../menu/MenuCardSubLabel";
import { EditorTileInspectorLayer } from "./EditorTileInspectorLayer";
import { getChangeableTileDataSnapshot, getEmptyChangeableTileDataSnapshot } from "../../../shared/game/TileDataModel";
import { getChangesToClearTilesToMakeWayForGroundTile, undoableMapEditorRemoveTiles, undoableMapEditorSwapTileLayers } from "../../stores/undo/operation/MapEditorSetTilesOp";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { UiConstants } from "../../data/UiConstants";
const SectionContainer = styled.div.withConfig({
  displayName: "EditorTileInspector__SectionContainer",
  componentId: "sc-1wgoiab-0"
})(["overflow:auto;border-radius:", ";border:solid 1px #888888;"], UiConstants.BORDER_RADIUS);
export const EditorTileInspector = observer(({
  mapEditorStore
}) => {
  const {
    t
  } = useTranslation();
  const groundTileData = mapEditorStore.selectedGroundTileDataWithGapsDirectOnly;
  const decorationTileDataNonFlat = mapEditorStore.selectedDecorationTileDataNonFlat;
  const decorationTileDataFlatX = mapEditorStore.selectedDecorationTileDataFlatX;
  const decorationTileDataFlatY = mapEditorStore.selectedDecorationTileDataFlatY;
  const decorationTileDataFlatZ = mapEditorStore.selectedDecorationTileDataFlatZ;
  const overlappingGroundTilesFromDifferentLayer = mapEditorStore.selectedOverlappingGroundTilesFromDifferentLayer;

  const moveGroundLayer = (tileData, newLayer) => {
    const {
      x,
      y,
      plane,
      layer: currentLayer
    } = tileData.position;
    const changes = new Array(); // Clear current tile

    changes.push({
      position: {
        x,
        y,
        layer: currentLayer
      },
      newData: getEmptyChangeableTileDataSnapshot()
    }); // Move tile to new layer

    changes.push({
      position: {
        x,
        y,
        layer: newLayer
      },
      newData: getChangeableTileDataSnapshot(tileData)
    }); // Clear all tiles that would be in the way of this new ground tile

    changes.push(...getChangesToClearTilesToMakeWayForGroundTile(mapEditorStore.currentMapStore, tileData.tileAssetId, x, y, plane, newLayer));
    undoableMapEditorSwapTileLayers(mapEditorStore.currentMapStore, plane, changes);
  };

  const deleteLayer = tileData => {
    const tileAssetToChange = [{
      position: tileData.position,
      newData: getEmptyChangeableTileDataSnapshot()
    }];
    undoableMapEditorRemoveTiles(mapEditorStore.currentMapStore, tileData.position.plane, tileAssetToChange);
  };

  const {
    complexityShowLayerMinusOne,
    complexityUseTileConflictResolution,
    selectedTilePosition
  } = mapEditorStore;
  const {
    x,
    y,
    plane
  } = selectedTilePosition;
  return /*#__PURE__*/React.createElement(MenuCard, null, /*#__PURE__*/React.createElement(MenuCardLabel, null, t("editor.tile_selected")), t("editor.position"), " ", `x: ${x} y: ${y} height: ${plane}`, mapEditorStore.currentMapStore.isUserAllowedToEditCurrentMap && /*#__PURE__*/React.createElement(React.Fragment, null, decorationTileDataNonFlat.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.tile_inspector_decoration_non_flat"), ":"), /*#__PURE__*/React.createElement(SectionContainer, null, decorationTileDataNonFlat.map((tileData, index) => tileData && /*#__PURE__*/React.createElement(EditorTileInspectorLayer, {
    mapEditorStore: mapEditorStore,
    key: index,
    tileData: tileData,
    swapUp: null,
    swapDown: null,
    delete: deleteLayer,
    showConflictResolutionOriginEditor: complexityUseTileConflictResolution,
    showConflictResolutionZIndexEditor: false,
    showOffsetOverride: true
  })))), decorationTileDataFlatX.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.tile_inspector_decoration_flat_x"), ":"), /*#__PURE__*/React.createElement(SectionContainer, null, decorationTileDataFlatX.map((tileData, index) => tileData && /*#__PURE__*/React.createElement(EditorTileInspectorLayer, {
    mapEditorStore: mapEditorStore,
    key: index,
    tileData: tileData,
    swapUp: null,
    swapDown: null,
    delete: deleteLayer,
    showConflictResolutionOriginEditor: false,
    showConflictResolutionZIndexEditor: complexityUseTileConflictResolution,
    showOffsetOverride: true
  })))), decorationTileDataFlatY.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.tile_inspector_decoration_flat_y"), ":"), /*#__PURE__*/React.createElement(SectionContainer, null, decorationTileDataFlatY.map((tileData, index) => tileData && /*#__PURE__*/React.createElement(EditorTileInspectorLayer, {
    mapEditorStore: mapEditorStore,
    key: index,
    tileData: tileData,
    swapUp: null,
    swapDown: null,
    delete: deleteLayer,
    showConflictResolutionOriginEditor: false,
    showConflictResolutionZIndexEditor: complexityUseTileConflictResolution,
    showOffsetOverride: true
  })))), decorationTileDataFlatZ.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.tile_inspector_decoration_flat_z"), ":"), /*#__PURE__*/React.createElement(SectionContainer, null, decorationTileDataFlatZ.map((tileData, index) => tileData && /*#__PURE__*/React.createElement(EditorTileInspectorLayer, {
    mapEditorStore: mapEditorStore,
    key: index,
    tileData: tileData,
    swapUp: null,
    swapDown: null,
    delete: deleteLayer,
    showConflictResolutionOriginEditor: false,
    showConflictResolutionZIndexEditor: complexityUseTileConflictResolution,
    showOffsetOverride: true
  })))), groundTileData.some(tileData => tileData != null) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.tile_inspector_ground"), ":"), /*#__PURE__*/React.createElement(SectionContainer, null, groundTileData.map((tileData, index) => (tileData || complexityShowLayerMinusOne) && /*#__PURE__*/React.createElement(EditorTileInspectorLayer, {
    mapEditorStore: mapEditorStore,
    key: index,
    index: index,
    tileData: tileData != null ? tileData : null,
    swapDown: index === 0 && complexityShowLayerMinusOne ? () => moveGroundLayer(tileData, tileData.position.layer - 1) : null,
    swapUp: index === 1 && complexityShowLayerMinusOne ? () => moveGroundLayer(tileData, tileData.position.layer + 1) : null,
    delete: deleteLayer,
    showConflictResolutionOriginEditor: false,
    showConflictResolutionZIndexEditor: false
  })))), overlappingGroundTilesFromDifferentLayer.some(tileData => tileData != null) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.tile_inspector_ground_overlapping"), ":"), /*#__PURE__*/React.createElement(SectionContainer, null, overlappingGroundTilesFromDifferentLayer.map((tileData, index) => /*#__PURE__*/React.createElement(EditorTileInspectorLayer, {
    mapEditorStore: mapEditorStore,
    key: index,
    index: index,
    tileData: tileData != null ? tileData : null,
    swapUp: null,
    swapDown: null,
    delete: deleteLayer,
    showConflictResolutionOriginEditor: true,
    showConflictResolutionZIndexEditor: false
  }))))));
});